<template>
    <div class="plant-details-holder">
        <div
            v-if="plantInfo || props.relatedStoretrackDetails"
            class="plant"
            :class="{
                'no-details': props.hidePlantDetails,
                'has-storetrack-and-is-compare': props.relatedStoretrackDetails,
                'has-storetrack-info': props.relatedStoretrackDetails && plantInfo,
                'only-storetrack-info': props.relatedStoretrackDetails && !plantInfo,
            }">
            <plant-details-title
                class="plant-name"
                v-model="overallPlantTitle"
                :relatedStoretrackDetails="props.relatedStoretrackDetails"
                :plantInfo="plantInfo"
                :basePlantName="props.relatedPlantDetails?.baseName"
                @open-project-in-modal="openProjectInModal" />
            <div class="plant-details" v-if="!hidePlantDetails">
                <div class="plant-types">
                    <metric-with-value
                        v-for="(value, key) in getHeaderProperties(technologyOrFuelValue, headerGenCapacity, headerStorageCapacity, headerDemCapacity, plantIsStorage, true)"
                        :key="key"
                        :metric="key"
                        :value="value" />
                </div>
                <div v-if="!props.hideTable" class="table-wrapper">
                    <table class="company-table no-highlight-on-hover">
                        <thead>
                            <tr>
                                <th v-for="(_, key) in companyNamesForTable" :key="key">
                                    {{ key }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td v-for="(values, key) in companyNamesForTable" :key="key">
                                    <div class="company-cell-wrapper">
                                        <template v-for="value in values" :key="value">
                                            <div v-if="!plantIsStorage || !allDataStore.trialUserCompanyLimits || !allDataStore.trialUserCompanyLimits[value]">
                                                {{ value }}
                                            </div>
                                            <primevue-button v-else text class="company-popout-link" @click="openPartnersDialog(value)">
                                                <div>{{ value }}</div>
                                            </primevue-button>
                                        </template>

                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="props.relatedStoretrackDetails" class="plant-types storetrack-info">
                    <metric-with-value metric="Segment" :value="props.relatedStoretrackDetails.customerSegment" />
                    <metric-with-value metric="Installation" :value="props.relatedStoretrackDetails.installation" />
                    <metric-with-value metric="Value Stream" :value="props.relatedStoretrackDetails.valueStreams ?? ['None']" />
                </div>
            </div>
            <div v-if="plantMapData" class="map">
                <leaflet-map
                    :zoom="5"
                    :hover="true"
                    plantHasSize
                    :show-popup="props.mapOpensModal"
                    :plantData="plantMapData"
                    :show-interconnectors="false"
                    :countryCode="props.relatedStoretrackDetails?.country || 'GB'"
                />
            </div>
            <div v-if="timelineEvents" class="project-timeline">
                <div class="timeline-header">Project Timeline</div>
                <timeline
                    :value="timelineEvents"
                    :layout="isMobileView ? 'vertical' : 'horizontal'"
                >
                    <template #content="slotProps">
                        <div class="timeline-inner-content">
                            <div class="timeline-inner-header">{{ slotProps.item.year }}</div>
                            <ul class="timeline-inner-body">
                                <li
                                    v-for="(event, index) in slotProps.item.events"
                                    :key="index"
                                >
                                    {{ event.description }} <span>({{ event.date }})</span>
                                </li>
                            </ul>
                        </div>

                    </template>
                </timeline>
            </div>
        </div>
        <div class="bm-unit-details" v-if="plantInfo && !props.hidePlantDetails && !props.hideTable">
            <tabs class="tabs bm-unit-tabs" v-model:value="activeBmUnitTab" v-if="bmUnitTabs" @update:value="activeBmUnitTabUpdated" scrollable>
                <tab-list>
                    <tab :value="tab.id" v-for="tab in bmUnitTabs" :key="tab.id">
                        {{ tab.label }}
                        <primevue-button text class="plant-id-copy-button" aria-label="Copy plant ID" @click.stop="copyPlantId(tab.id)">
                            <font-awesome-icon :icon="['fas', 'copy']" />
                        </primevue-button>
                    </tab>
                </tab-list>
            </tabs>
            <div class="bm-specific-unit">
                <div class="bm-metrics">
                    <metric-with-value v-if="plantInfo.name !== overallPlantTitle" metric="BMU unit name" :value="plantInfo?.name" />
                    <metric-with-value
                        v-for="(value, key) in bmHeaderProperties"
                        :key="key"
                        :metric="key"
                        :value="value" />
                </div>
                <div class="bm-specific-data" v-if="plantInfo">
                    <bm-unit-data
                        :plantBmuId="activeBmUnitTab"
                        :plantInfo="plantInfo"
                        :date-override="props.dateOverride"
                        :chart-date-range="props.chartDateRange"
                        :initial-details-tab="props.initialDetailsTab"
                    />
                </div>
            </div>
        </div>
    </div>
    <central-modal
        v-if="companyOpenInModal"
        :header="companyOpenInModal"
        v-model:visible="modalVisible"
        hide-header
        size="medium"
    >
        <company-panel :selected-company="companyOpenInModal" :lookAtAllDataProp="true" />
    </central-modal>
    <central-modal
        v-if="projectForModal"
        :header="projectForModal.projectName"
        v-model:visible="projectModalVisible"
        hide-header
        size="medium"
    >
        <project-panel-redacted-data :selectedProject="projectForModal" />
    </central-modal>
</template>

<script setup lang="ts">
import {
    ref, computed, watch,
} from 'vue';

import { IPlantDetails, IEnactPlants } from '@/interfaces/powerMarkets/plant/IPlantDetails.ts';
import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import { ITimelineByYearItem } from '@/interfaces/storageTracking/ITimelineObject.ts';
import { IIdLabel } from '@/interfaces/storageForecasting/IIdLabel.ts';
import { IRedactedProjectData } from '@/interfaces/storageTracking/IRedactedProjectData.ts';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';

import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import PrimevueButton from 'primevue/button';

import LeafletMap from '@/components/powerMarkets/common/LeafletMap.vue';
import MetricWithValue from '@/components/common/MetricWithValue.vue';
import Timeline from 'primevue/timeline';
import CompanyPanel from '@/components/storageTracking/companySearch/CompanyPanel.vue';
import ProjectPanelRedactedData from '@/components/storageTracking/projectSearch/ProjectPanelRedactedData.vue';
import { formatStatusesAndRolesForTimelineByYear } from '@/utils/storageTracking/ProjectStatus.ts';
import { useAllDataStore } from '@/stores/storageTracking/AllDataStore.ts';
import CentralModal from '@/components/common/CentralModal.vue';

import { getHeaderProperties, usePlantDetails, useMultiPlantDetails } from '@/composables/powerMarkets/usePlantDetails.ts';
import { plantApi } from '@/api/powerMarkets/plantApi.ts';
import { useSignalRLifecycle } from '@/realTimeConnection/SignalRLifecycle.ts';

import { SCREEN_SIZES } from '@/constants/ScreenSizes.ts';
import { useWindowSize } from '@vueuse/core';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import { useToast } from 'primevue/usetoast';
import BmUnitData from './BmUnitData.vue';
import PlantDetailsTitle from './PlantDetailsTitle.vue';

const props = withDefaults(
    defineProps<{
        plantId: string;
        hideTable?: boolean;
        hidePlantDetails?: boolean;
        plantDetails?: IPlantDetails | null;
        relatedPlantDetails?: IEnactPlants | null;
        relatedStoretrackDetails?: ICsvData | null;
        dateOverride?: IDashboardOverrides;
        chartDateRange?: IDateRange;
        mapOpensModal?: boolean;
        initialDetailsTab?: PlantDetailsTab;
    }>(),
    {
        plantDetails: null,
        relatedPlantDetails: null,
        relatedStoretrackDetails: null,
        dateOverride: undefined,
        chartDateRange: undefined,
        mapOpensModal: false,
        initialDetailsTab: undefined,
    },
);

const allDataStore = useAllDataStore();
const { width } = useWindowSize();
const signalRLifecycle = useSignalRLifecycle();
const toast = useToast();

const plantInfo = ref<IPlantDetails | null>(props.plantDetails);
const overallPlantTitle = ref<string>('');
const activeBmUnitTab = ref<string>('');
const companyOpenInModal = ref<string | null>(null);
const modalVisible = ref<boolean>(false);
const projectModalVisible = ref<boolean>(false);
const projectForModal = ref<IRedactedProjectData | null>(null);
const isMobileView = computed(() => width.value < SCREEN_SIZES.MOBILE_MAX_WIDTH);

const bmHeaderProperties = computed<Record<string, string | number>>(() => (
    getHeaderProperties(
        technologyOrFuelValue.value,
        plantInfo.value?.generationCapacity,
        plantInfo.value?.batteryEnergy,
        plantInfo.value?.demandCapacity,
        plantIsStorage.value,
        false,
        true,
        isMultipleBmUnitsView.value,
        decommissioningStatus.value,
        plantInfo.value?.lastZero,
        plantInfo.value?.zone,
        plantInfo.value?.extraInfo,
    )
));

const {
    technologyOrFuelValue, plantIsStorage, isMultipleBmUnitsView, decommissioningStatus,
} = usePlantDetails(plantInfo.value, props.relatedPlantDetails, props.relatedStoretrackDetails);

const { plantMapData } = useMultiPlantDetails({
    [props.plantId]: {
        plantInfo: props.plantDetails,
        relatedEnactData: props.relatedPlantDetails,
        relatedStoretrackDetails: props.relatedStoretrackDetails,
    },
});

async function copyPlantId (plantId: string): Promise<void> {
    try {
        await navigator.clipboard.writeText(plantId);
        toast.add({
            severity: 'success',
            summary: 'Plant ID copied',
            detail: 'Plant ID copied to clipboard',
            life: 3000,
        });
    } catch {
        toast.add({
            severity: 'error',
            summary: 'Copy failed',
            detail: 'Unable to copy Plant ID to clipboard',
            life: 3000,
        });
    }
}

function openPartnersDialog (value: string): void {
    companyOpenInModal.value = value;
    modalVisible.value = true;
}

function openProjectInModal (project: IRedactedProjectData): void {
    projectModalVisible.value = true;
    projectForModal.value = project;
}

function handleZeroPnUpdate (): void {
    const plantId = activeBmUnitTab.value;
    if (!plantInfo.value || !plantId) return;

    plantApi.getLastZeroPn(plantId).then((response) => {
        if (!response.data) return;
        plantInfo.value = { ...plantInfo.value!, lastZero: response.data };
    });
}

watch(() => modalVisible.value, newState => {
    if (newState) return;
    companyOpenInModal.value = null;
});

const bmUnitTabs = computed<Array<IIdLabel>>(() => {
    if (!props.relatedPlantDetails || props.relatedPlantDetails.plants.length === 0) return [];

    const plantsUnsorted = props.relatedPlantDetails.plants.map((plant) => ({ id: plant.id, label: plant.id }));

    const plantsSorted = plantsUnsorted.sort((plant1, plant2) => {
        const getNumber = (str: string): number => {
            const parts = str?.split('-');
            const last = parts?.[parts.length - 1];
            return parseInt(last, 10);
        };
        const plant1Number = getNumber(plant1.label);
        const plant2Number = getNumber(plant2.label);
        if (Number.isNaN(plant1Number) || Number.isNaN(plant2Number)) return plant1.label.localeCompare(plant2.label);
        return plant1Number - plant2Number;
    });

    return plantsSorted;
});

const timelineEvents = computed<Array<ITimelineByYearItem> | null>(() => {
    if (props.relatedStoretrackDetails === null) return null;

    return formatStatusesAndRolesForTimelineByYear(props.relatedStoretrackDetails);
});

const headerGenCapacity = computed<number | null | undefined>(() => {
    if (plantIsStorage.value && props.relatedStoretrackDetails) {
        if (props.relatedStoretrackDetails.powerCapacityMw !== 0) return props.relatedStoretrackDetails.powerCapacityMw;
    }

    return getCapacityAggregation((plant: IPlantDetails) => plant.generationCapacity);
});

const headerDemCapacity = computed<number | null | undefined>(() => getCapacityAggregation((plant: IPlantDetails) => plant.demandCapacity));

const headerStorageCapacity = computed<number | null | undefined>(() => {
    if (plantIsStorage.value && props.relatedStoretrackDetails) {
        if (props.relatedStoretrackDetails.storageCapacityMwH !== 0) return props.relatedStoretrackDetails.storageCapacityMwH;
    }

    return getCapacityAggregation((plant: IPlantDetails) => plant.batteryEnergy);
});

const companyNamesForTable = computed<Record<string, string[]>>(() => {
    const companiesTableData: Record<string, string[]> = {};
    if (
        props.relatedStoretrackDetails
        && props.relatedStoretrackDetails.latestData.ownersForSearch
        && props.relatedStoretrackDetails.latestData.ownersForSearch.length > 0
    ) {
        companiesTableData.Owner = props.relatedStoretrackDetails.latestData.ownersForSearch;
    } else if (plantInfo.value && plantInfo.value.owner && plantInfo.value.owner !== 'Unknown') {
        companiesTableData.Owner = [plantInfo.value.owner];
    }

    if (
        props.relatedStoretrackDetails
        && props.relatedStoretrackDetails.latestData.tradersForSearch
        && props.relatedStoretrackDetails.latestData.tradersForSearch.length > 0
    ) {
        companiesTableData.Optimiser = props.relatedStoretrackDetails.latestData.tradersForSearch;
    } else if (plantInfo.value && plantInfo.value.optimiser) {
        companiesTableData.Optimiser = [plantInfo.value.optimiser];
    }

    if (!props.relatedStoretrackDetails) return companiesTableData;

    if (
        props.relatedStoretrackDetails.latestData.operatorsForSearch
        && props.relatedStoretrackDetails.latestData.operatorsForSearch.length > 0
    ) {
        companiesTableData.Operator = props.relatedStoretrackDetails.latestData.operatorsForSearch;
    }

    if (
        props.relatedStoretrackDetails.latestData.developersSearch
        && props.relatedStoretrackDetails.latestData.developersSearch.length > 0
    ) {
        companiesTableData.Developer = props.relatedStoretrackDetails.latestData.developersSearch;
    }

    if (
        props.relatedStoretrackDetails.latestData.technologyProvidersForSearch
        && props.relatedStoretrackDetails.latestData.technologyProvidersForSearch.length > 0
    ) {
        companiesTableData['Technology Provider'] = props.relatedStoretrackDetails.latestData.technologyProvidersForSearch;
    }

    if (
        props.relatedStoretrackDetails.latestData.financersSearch
        && props.relatedStoretrackDetails.latestData.financersSearch.length > 0
    ) {
        companiesTableData.Financer = props.relatedStoretrackDetails.latestData.financersSearch;
    }

    return companiesTableData;
});

function getCapacityAggregation (getCapacityFunc: (plant: IPlantDetails) => number): number | null | undefined {
    if (isMultipleBmUnitsView.value) {
        const sumOfPowerCapacities = props.relatedPlantDetails?.plants.reduce((sum, plant) => sum + getCapacityFunc(plant), 0);
        return sumOfPowerCapacities === 0 ? null : sumOfPowerCapacities;
    }
    if (!plantInfo.value) return null;
    return getCapacityFunc(plantInfo?.value);
}

function activeBmUnitTabUpdated (): void {
    const selectedPlant = props.relatedPlantDetails?.plants.find((plant) => plant.id === activeBmUnitTab.value);
    if (selectedPlant) {
        plantInfo.value = selectedPlant;
        handleZeroPnUpdate();
    }
}

async function initialise (): Promise<void> {
    plantInfo.value = props.plantDetails;
    activeBmUnitTab.value = props.plantId;
    signalRLifecycle.listenTo('zeroPnUpdate', handleZeroPnUpdate);
    handleZeroPnUpdate();
}

watch(() => props.plantId, async () => initialise());

initialise();
</script>

<style lang="scss" scoped>
@use '/src/assets/scss/components/powerMarkets/europePlants.scss';

.plant-details-holder {
    background: var(--neutral0);
    padding: var(--largeSpacing);
    min-height: 100px;
}
.plant {
    display: grid;
    grid-template-columns: 3fr minmax(300px, 1fr);
    grid-template-rows: auto 1fr auto auto;
    grid-template-areas:
        'plant-name map'
        'plant-details map';
    height: fit-content;

    &.no-table {
        grid-template-areas: 'plant-name' 'plant-details';
    }
    &.no-details {
        grid-template-areas: '.';
    }

    &.has-storetrack-info {
        grid-template-areas:
            'plant-name map'
            'plant-details map'
            'project-timeline project-timeline';
        grid-template-rows: auto 1fr auto auto auto;
    }

    &.only-storetrack-info {
        grid-template-areas:
            'plant-name map'
            'plant-details map'
            'project-timeline project-timeline';

        grid-template-rows: auto 1fr auto;
    }

    @include responsive('sm') {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr auto auto;
        grid-template-areas:
            'plant-name'
            'plant-details'
            'project-timeline'
            'map';
        &.has-storetrack-info,
        &.only-storetrack-info,
        &.no-map-or-timeline,
        &.no-table,
        &.no-details {
            grid-template-columns: 1fr;
            grid-template-rows: auto 1fr auto auto;
            grid-template-areas:
                'plant-name'
                'plant-details'
                'project-timeline'
                'map';
        }
    }

    .map {
        grid-area: map;
        min-height: 400px;
        padding: 0 0 var(--defaultSpacing) var(--defaultSpacing);
    }

    .button-content {
        display: flex;
        flex-direction: row;
        gap: var(--defaultSpacing);
        align-items: center;
        justify-content: space-between;
        text-wrap: nowrap;
    }
}

.plant-name {
    grid-area: plant-name;
}

.plant-details {
    grid-area: plant-details;
    align-self: start;
    display: grid;
    gap: var(--defaultSpacing);
    padding-bottom: var(--defaultSpacing);

    .table-wrapper {
        overflow-x: auto;
    }
    .company-table {
        thead {
            position: static;
            th {
                text-align: left;
            }
        }
        tr {
            td {
                align-content: start;
                .company-cell-wrapper {
                    display: flex;
                    flex-direction: column;
                    font-size: var(--smallFontSize);
                }
                .company-popout-link {
                    width: fit-content;
                    font-size: var(--smallFontSize);
                    padding: 0;
                    border: 0;
                }
            }
        }

    }
}

.bm-unit-details {
    grid-area: bm-unit-details;
    top: 0;
    z-index: 10;
    height: fit-content;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--neutral100);
    align-self: end;
    .bm-unit-tabs {
        background-color: var(--neutral0);
        font-size: var(--mediumFontSize);
    }
    .bm-specific-unit {
        border-bottom: 1px solid var(--p-tabs-tablist-border-color);
        border-right: 1px solid var(--p-tabs-tablist-border-color);
        border-left: 1px solid var(--p-tabs-tablist-border-color);
        .bm-metrics {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 120px), 1fr));
            gap: var(--defaultSpacing);
            height: fit-content;
            padding: var(--largeSpacing) var(--defaultSpacing) 0 var(--defaultSpacing);
        }
        .bm-specific-data {
            display: grid;
            grid-area: bm-specific-data;
            gap: var(--defaultSpacing);
            align-content: start;
            background-color: var(--neutral100);
            padding: var(--defaultSpacing);
        }
    }
    .p-tabs.p-component {
        max-width: 100%;
    }
    .p-tab-active{
        border-top: 1px solid var(--p-tabs-tablist-border-color);
        border-right: 1px solid var(--p-tabs-tablist-border-color);
        border-left: 1px solid var(--p-tabs-tablist-border-color);
    }
}

.project-timeline {
    grid-area: project-timeline;
    background-color: var(--neutral300);
    border-radius: var(--borderRadiusOuter);
    padding: var(--defaultSpacing);
    margin-bottom: var(--defaultSpacing);
    height: fit-content;
    .timeline-header {
        font-weight: var(--boldFontWeight);
        padding-bottom: var(--smallSpacing);
    }

    .p-timeline {
        height: 100%;

        :deep(.p-timeline-event) {
            flex-grow: 1;
        }

        :deep(.p-timeline-event-content) {
            padding: 0;
        }

        :deep(.p-timeline-event-opposite) {
            display: none;
        }

        :deep(.p-timeline-event-marker::before) {
            background-color: var(--accentColour);
        }

        .timeline-inner-content {
            padding: var(--smallSpacing);
            margin-left: var(--extraSmallSpacing);
            border-radius: var(--borderRadiusInner);
            width: fit-content;

            &:hover {
                background-color: var(--neutral200);
            }

            .timeline-inner-header {
                margin-bottom: var(--smallSpacing);
                color: var(--accentColour);
                font-weight: var(--boldFontWeight);
            }

            .timeline-inner-body {
                font-size: var(--smallFontSize);
                list-style-type: disc;
                padding-left: var(--largeSpacing);
                span {
                    font-weight: var(--lightFontWeight);
                }
            }
        }
    }
}

:deep(.clickable) {
    color: var(--actionColour);
}

.plant-types {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 120px), 1fr));
    gap: var(--defaultSpacing);
    height: fit-content;
    @include responsive('sm') {
        grid-template-columns: 1fr 1fr;
    }
}

:deep(.p-button-text) {
    background: transparent;

    &:hover {
        text-decoration: underline;
    }
}

.has-storetrack-and-is-compare {
    .storetrack-info {
        grid-template-columns: repeat(3, 1fr);
    }
}

.metric-holder-wrapper {
    display: grid;
    gap: var(--defaultSpacing);
}

.tabs {
    overflow-x: hidden;
    position: sticky;
    top: 0;
    z-index: 1000;
    .p-tab-active {
        background-color: var(--neutral100);
    }
    :deep(.p-tablist-active-bar) {
        background: transparent;
    }
}

.arrow-icon-wrapper {
    width: 100%;
    height: 100%;
    background: 'transparent';
    display: flex;
    justify-content: center;
    align-items: center;

    .icon {
        color: var(--actionColour);
        opacity: 0;
    }
}

</style>
